import { Routes } from '@angular/router';

import { AppLayoutComponent } from './core/layout/app-layout/app-layout.component';
import { CallbackComponent } from './core/auth/callback/callback.component';
import { authGuard } from './core/auth/auth.guard';
import { permissionChildGuard } from './core/auth/permission.guard';
import { devOnlyGuard } from './core/auth/dev-only.guard';
import { HomeComponent } from './features/home/home.component';
import { HOME_PERMISSIONS } from './features/home/home.permissions';
import { SOURCES_PERMISSIONS } from './features/sources/sources.permissions';
import { SCHEDULING_PERMISSIONS } from './features/scheduling/scheduling.permissions';
import { UPLOADS_PERMISSIONS } from './features/uploads/uploads.permissions';
import { ERP_AUDIT_PERMISSIONS } from './features/erp-audit/erp-audit.permissions';
import { CONTROL_CENTER_PERMISSIONS } from './features/control-center/control-center.permissions';

export const routes: Routes = [
  {
    path: 'test-root',
    component: HomeComponent
  },
  {
    path: 'callback',
    component: CallbackComponent
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [permissionChildGuard],
    children: [
      // ── Maestros ──
      {
        path: 'maestros/clientes',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/clients/clients.routes').then(m => m.CLIENTS_ROUTES)
      },
      {
        path: 'maestros/sedes',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/client-sites/client-sites.routes').then(m => m.CLIENT_SITES_ROUTES)
      },
      {
        path: 'maestros/entidades-financieras',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/financial-institutions/financial-institutions.routes').then(
            m => m.FINANCIAL_INSTITUTIONS_ROUTES
          )
      },
      {
        path: 'maestros/cuentas-bancarias',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/bank-accounts/bank-accounts.routes').then(m => m.BANK_ACCOUNTS_ROUTES)
      },
      {
        path: 'maestros/convenios',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/agreements/agreements.routes').then(m => m.AGREEMENTS_ROUTES)
      },
      {
        path: 'maestros/contratos-servicio',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/contracts/contracts.routes').then(m => m.CONTRACTS_ROUTES)
      },
      {
        path: 'maestros/contratos-operativos',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/operational-contracts/operational-contracts.routes').then(
            m => m.OPERATIONAL_CONTRACTS_ROUTES
          )
      },
      {
        path: 'maestros/asociaciones-convenios',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/agreement-associations/agreement-associations.routes').then(
            m => m.AGREEMENT_ASSOCIATIONS_ROUTES
          )
      },
      {
        path: 'maestros/calendario',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/business-calendar/business-calendar.routes').then(
            m => m.BUSINESS_CALENDAR_ROUTES
          )
      },
      {
        path: 'maestros/politicas-liquidacion',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/settlement-policies/settlement-policies.routes').then(
            m => m.SETTLEMENT_POLICIES_ROUTES
          )
      },
      {
        path: 'maestros/migracion-conectores',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/canonical-rollout/canonical-rollout.routes').then(
            m => m.CANONICAL_ROLLOUT_ROUTES
          )
      },
      {
        path: 'maestros/fuentes-bancarias',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/operational-sources/operational-sources.routes').then(
            m => m.BANK_SOURCE_MASTER_ROUTES
          )
      },
      {
        path: 'maestros/adquirentes',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/operational-sources/operational-sources.routes').then(
            m => m.ACQUIRER_MASTER_ROUTES
          )
      },
      {
        path: 'maestros/agregadores',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/operational-sources/operational-sources.routes').then(
            m => m.AGGREGATOR_MASTER_ROUTES
          )
      },
      {
        path: 'maestros/fuentes-pos',
        data: { permission: 'masters.read' },
        loadChildren: () =>
          import('./features/masters/operational-sources/operational-sources.routes').then(
            m => m.POS_SOURCE_MASTER_ROUTES
          )
      },

      // ── Fuentes (sources) ──
      {
        path: 'maestros/fuentes-conciliacion',
        data: { permission: SOURCES_PERMISSIONS.reconciliation.read },
        loadChildren: () =>
          import('./features/sources/reconciliation-sources/reconciliation-sources.routes').then(
            m => m.RECONCILIATION_SOURCES_ROUTES
          )
      },
      {
        path: 'maestros/source-records',
        data: { permission: SOURCES_PERMISSIONS.sourceRecords.read },
        loadChildren: () =>
          import('./features/sources/source-records/source-records.routes').then(
            m => m.SOURCE_RECORDS_ROUTES
          )
      },
      {
        path: 'pos/transacciones',
        data: { permission: SOURCES_PERMISSIONS.posTransactions.read },
        loadChildren: () =>
          import('./features/sources/pos-transactions/pos-transactions.routes').then(
            m => m.POS_TRANSACTIONS_ROUTES
          )
      },
      {
        path: 'pos/cierres',
        data: { permission: SOURCES_PERMISSIONS.posClosures.read },
        loadChildren: () =>
          import('./features/sources/pos-closures/pos-closures.routes').then(
            m => m.POS_CLOSURES_ROUTES
          )
      },
      {
        path: 'pos/agregadores',
        data: { permission: SOURCES_PERMISSIONS.aggregators.read },
        loadChildren: () =>
          import('./features/sources/aggregators/aggregators.routes').then(
            m => m.AGGREGATORS_ROUTES
          )
      },
      {
        path: 'pos/adquiriencias',
        data: { permission: SOURCES_PERMISSIONS.acquirings.read },
        loadChildren: () =>
          import('./features/sources/acquirings/acquirings.routes').then(
            m => m.ACQUIRINGS_ROUTES
          )
      },
      {
        path: 'pos/movimientos-bancarios',
        data: { permission: SOURCES_PERMISSIONS.reconciliation.read },
        loadChildren: () =>
          import('./features/sources/bank-transactions/bank-transactions.routes').then(
            m => m.BANK_TRANSACTIONS_ROUTES
          )
      },

      // ── Auditoría ERP ──
      {
        path: 'maestros/erp-events',
        data: { permission: ERP_AUDIT_PERMISSIONS.read },
        loadChildren: () =>
          import('./features/erp-audit/erp-sync-events/erp-sync-events.routes').then(
            m => m.ERP_SYNC_EVENTS_ROUTES
          )
      },

      // ── Conciliación ──
      {
        path: 'reconciliation',
        data: { permission: 'reconciliation.read' },
        loadChildren: () =>
          import('./features/reconciliation/reconciliation.routes').then(m => m.RECONCILIATION_ROUTES)
      },
      {
        path: 'reports',
        data: { permission: 'financial-reconciliation.read' },
        loadComponent: () =>
          import('./features/reports/pages/reports-page/reports-page.component').then(
            m => m.ReportsPageComponent
          )
      },
      {
        // Tablero gerencial: el menú solo aparece si el contrato tiene el perfil MANAGEMENT_DASHBOARD activo.
        path: 'dashboard-gerencial',
        data: { permission: 'financial-reconciliation.read' },
        loadChildren: () =>
          import('./features/management-dashboard/management-dashboard.routes').then(m => m.MANAGEMENT_DASHBOARD_ROUTES)
      },

      // ── Programación (scheduling) ──
      {
        path: 'paybridge/schedules',
        data: { permission: SCHEDULING_PERMISSIONS.read },
        loadChildren: () =>
          import('./features/scheduling/scheduling.routes').then(m => m.SCHEDULING_ROUTES)
      },

      // ── Cargue (uploads) ──
      {
        path: 'paybridge/file-upload',
        data: { permission: UPLOADS_PERMISSIONS.read },
        loadChildren: () =>
          import('./features/uploads/uploads.routes').then(m => m.UPLOADS_ROUTES)
      },

      // ── Centro de control / Tenant (solo usuario de desarrollo) ──
      {
        path: 'paybridge/config',
        canActivate: [devOnlyGuard],
        data: { permission: CONTROL_CENTER_PERMISSIONS.read },
        loadChildren: () =>
          import('./features/control-center/control-center.routes').then(m => m.CONTROL_CENTER_ROUTES)
      },
      {
        path: 'paybridge/tenants',
        canActivate: [devOnlyGuard],
        data: { permission: CONTROL_CENTER_PERMISSIONS.manage },
        loadComponent: () =>
          import('./features/control-center/tenants/tenants-page.component').then(
            m => m.TenantsPageComponent
          )
      },
      {
        path: 'paybridge/sources/new',
        canActivate: [devOnlyGuard],
        data: { permission: CONTROL_CENTER_PERMISSIONS.manage },
        loadComponent: () =>
          import('./features/control-center/sources/new-source-wizard.component').then(
            m => m.NewSourceWizardComponent
          )
      },
      {
        path: 'paybridge/demo',
        canActivate: [devOnlyGuard],
        data: { permission: CONTROL_CENTER_PERMISSIONS.manage },
        loadComponent: () =>
          import('./features/control-center/demo/demo-bootstrap.component').then(
            m => m.DemoBootstrapComponent
          )
      },
      {
        path: 'paybridge/demo/scenarios',
        canActivate: [devOnlyGuard],
        data: { permission: CONTROL_CENTER_PERMISSIONS.manage },
        loadComponent: () =>
          import('./features/control-center/demo/mock-scenarios.component').then(
            m => m.MockScenariosComponent
          )
      },
      {
        path: 'paybridge',
        pathMatch: 'full',
        redirectTo: 'paybridge/config'
      },

      // ── Acceso Denegado ──
      {
        path: 'unauthorized',
        loadComponent: () => import('./core/auth/unauthorized/unauthorized').then(m => m.UnauthorizedComponent)
      },

      // ── Inicio ──
      {
        path: '',
        pathMatch: 'full',
        component: HomeComponent
      }
    ]
  }
];
